import { useEffect, useState } from 'react';
import BloomreachAssets from '../../atom/BloomreachAssets';
import Headlines from '../../atom/Headlines';
import LinkHelper from '@mediashop/app/helper/LinkHelper';
import BackgroundColorWrapper from '../../atom/BackgroundColorWrapper';
import { BrXImageVariant } from '@mediashop/base/types/imageVariants';
import { EMPTY_STRING, SKIP_RENDER } from '@mediashop/app/constants/semanticConstants';
import { BackgroundImageWithTextProps } from './index';
import useDeviceType from '@mediashop/app/hooks/useDeviceType';
import { BrxImageWithAltText } from '@mediashop/app/bloomreach/types';
import CTAButton from '../../atom/CTAButton';
import { useNavigate } from '@mediashop/app/hooks/useNavigate';

const componentName = 'background-image-with-text';

/**
 * Component with a background of photo including header and checkout button.
 * In mobile view if the mobile photo is not provided, it use desktop photo but cropped desktop photo.
 */
function BackgroundImageWithText({
    headlines,
    image,
    mobileImage,
    callToActionButton,
    background = 'secondary',
}: BackgroundImageWithTextProps): JSX.Element {
    const navigate = useNavigate();
    const deviceType = useDeviceType();
    const isMobile = deviceType === 'mobile';
    const [usedImage, setUsedImage] = useState<BrxImageWithAltText | undefined>();

    useEffect(() => {
        setUsedImage(isMobile ? mobileImage : image);
    }, [image, isMobile, mobileImage]);

    const handleCallToActionButtonClick = (reference: string) => {
        if (reference.startsWith('#')) {
            // Anchor
            document?.getElementById(reference.replace('#', ''))?.scrollIntoView();
        } else if (LinkHelper.isAbsoluteUrl(reference)) {
            // Absolute link
            window.open(reference);
        } else {
            // Relative path
            navigate(reference);
        }
    };

    return (
        <BackgroundColorWrapper backgroundColor={background}>
            <article className={componentName}>
                <section className={`${componentName}__container`}>
                    {usedImage && (
                        <BloomreachAssets
                            className={componentName}
                            altText={usedImage.altText ?? EMPTY_STRING}
                            desktopImage={image}
                            mobileImage={mobileImage}
                            variant={BrXImageVariant.BackgroundImageWithText}
                        />
                    )}
                    {headlines?.headline || callToActionButton ? (
                        <div
                            className={
                                mobileImage
                                    ? `${componentName}__text-container`
                                    : `${componentName}__text-container-mobile`
                            }
                        >
                            {/** Headline */}
                            {headlines?.headline ? <Headlines {...headlines} /> : SKIP_RENDER}

                            {/** CTA button */}
                            {callToActionButton ? (
                                <CTAButton
                                    className={`${componentName}__cta-button`}
                                    style="primary"
                                    ctaButton={callToActionButton}
                                    onClick={() => handleCallToActionButtonClick(callToActionButton.reference)}
                                >
                                    {callToActionButton.text}
                                </CTAButton>
                            ) : (
                                SKIP_RENDER
                            )}
                        </div>
                    ) : (
                        SKIP_RENDER
                    )}
                </section>
            </article>
        </BackgroundColorWrapper>
    );
}

export default BackgroundImageWithText;
